import React, { useMemo, useState } from 'react';
import { Dropdown, Button } from 'react-bootstrap';
import { useTheme } from '../../contexts/ThemeContext';
import useIsMobile from '../../hooks/useIsMobile';
import FixedPositionDropdownMenu from './FixedPositionDropdownMenu';

/**
 * Reusable themed select matching order-entry dropdown styling.
 * Options may include `{ header: true, label }` group separators (not selectable).
 * Uses fixed popper strategy so menus aren't clipped by overflow parents.
 */
const ThemedSelect = ({
  value,
  options = [],
  onChange,
  width = 110,
  minWidth = 120,
  height = 32,
  fontSize = '0.85rem',
  fontWeight = 400,
  fitContent = false,
  borderColor: borderColorProp,
  'aria-label': ariaLabel,
  labelFormatter,
  testId,
  'data-testid': dataTestId,
}) => {
  const isMobile = useIsMobile(768, 1024);
  const { colors } = useTheme();
  const [open, setOpen] = useState(false);
  const resolvedTestId = dataTestId ?? testId;

  const normalized = useMemo(() => options.map((o, i) => {
    if (typeof o === 'string') return { value: o, label: o, disabled: false, header: false };
    if (o?.header) {
      return {
        value: o.value || `__header_${i}_${o.label}`,
        label: o.label || '',
        header: true,
        disabled: true,
      };
    }
    return {
      value: o.value,
      label: o.label || o.value,
      disabled: Boolean(o.disabled),
      header: Boolean(o.header),
    };
  }), [options]);

  const current = normalized.find((o) => !o.header && !o.disabled && String(o.value) === String(value))
    || normalized.find((o) => !o.header && !o.disabled)
    || normalized.find((o) => !o.header)
    || { value: '', label: '' };
  const toggleHeight = typeof height === 'number' ? height : 32;
  const borderColor = borderColorProp || colors.border || 'rgba(255, 255, 255, 0.15)';
  const bgInput = colors.bgInput || colors.bgSecondary || colors.bgCard;
  const textColor = colors.text || colors.white || '#fff';
  const displayLabel = labelFormatter ? labelFormatter(current) : current.label;
  // Hug the selected label; menu still needs room for longer options.
  const fitToggleWidth = fitContent
    ? Math.max(52, Math.ceil(String(displayLabel || '').length * 7.2 + 34))
    : null;
  // Percentage minWidth is valid on the toggle wrapper, but not on a `position: fixed`
  // menu — the containing block is the viewport, so `minWidth: '100%'` spans the screen.
  const numericMinWidth = typeof minWidth === 'number' ? minWidth : undefined;
  const menuMinWidth = fitContent
    ? Math.max(
      fitToggleWidth || 0,
      ...normalized.filter((o) => !o.header).map((o) => Math.ceil(String(o.label || '').length * 7.2 + 28)),
      numericMinWidth || 0,
    )
    : numericMinWidth;

  return (
    <Dropdown
      show={open}
      onToggle={(next) => setOpen(Boolean(next))}
      autoClose="outside"
      style={{ width: fitContent ? fitToggleWidth : width, flexShrink: fitContent ? 0 : undefined }}
      data-testid={resolvedTestId}
    >
      <Dropdown.Toggle
        as={Button}
        variant="outline-custom"
        aria-label={ariaLabel}
        style={{
          backgroundColor: bgInput,
          borderColor,
          color: textColor,
          padding: fitContent ? '4px 8px' : '4px 10px',
          height: toggleHeight,
          minHeight: toggleHeight,
          maxHeight: toggleHeight,
          lineHeight: '22px',
          fontSize,
          fontWeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: fitContent ? 4 : undefined,
          width: fitContent ? fitToggleWidth : '100%',
          textAlign: 'left',
          boxShadow: 'none',
          outline: 'none',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {displayLabel}
      </Dropdown.Toggle>
      <FixedPositionDropdownMenu
        sameWidth={!fitContent}
        offset={[0, 6]}
        padding={8}
        style={{
          backgroundColor: colors.bgSecondary || colors.bgCard,
          borderRadius: 8,
          border: `1px solid ${borderColor}`,
          padding: '4px 0',
          ...(fitContent ? { width: 'auto' } : {}),
          ...(menuMinWidth != null ? { minWidth: menuMinWidth } : {}),
          fontSize: '0.85rem',
          zIndex: 10060,
          maxHeight: isMobile ? 'min(45vh, 280px)' : 180,
          overflowY: 'auto',
        }}
      >
        {normalized.map((opt) => (
          opt.header ? (
            <Dropdown.Header
              key={opt.value}
              style={{
                color: colors.textMuted || '#888',
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                padding: '8px 14px 4px',
                marginBottom: 0,
              }}
            >
              {opt.label}
            </Dropdown.Header>
          ) : (
            <Dropdown.Item
              key={opt.value}
              disabled={opt.disabled}
              onClick={() => {
                if (opt.disabled) return;
                onChange?.(opt.value);
                setOpen(false);
              }}
              style={{
                color: opt.disabled
                  ? colors.textMuted
                  : (String(opt.value) === String(value) ? colors.primary : textColor),
                padding: opt.disabled ? '6px 14px 4px' : '8px 14px',
                backgroundColor: !opt.disabled && String(opt.value) === String(value)
                  ? `${colors.primary}1A`
                  : 'transparent',
                fontWeight: opt.disabled
                  ? 600
                  : (String(opt.value) === String(value) ? 600 : 400),
                cursor: opt.disabled ? 'default' : 'pointer',
                whiteSpace: 'nowrap',
                fontSize: opt.disabled ? '0.7rem' : undefined,
                textTransform: opt.disabled ? 'uppercase' : undefined,
                letterSpacing: opt.disabled ? '0.04em' : undefined,
                opacity: opt.disabled ? 0.75 : 1,
              }}
              onMouseEnter={(e) => {
                if (opt.disabled || String(opt.value) === String(value)) return;
                e.currentTarget.style.backgroundColor = `${colors.primary}0D`;
                e.currentTarget.style.color = colors.primary;
              }}
              onMouseLeave={(e) => {
                if (opt.disabled || String(opt.value) === String(value)) return;
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = textColor;
              }}
            >
              {opt.label}
            </Dropdown.Item>
          )
        ))}
      </FixedPositionDropdownMenu>
    </Dropdown>
  );
};

export default ThemedSelect;
